import { Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../../../components/atoms/button/button';
import { Modal } from '../../../../components/molecules/modal/modal';
import { Permission, groupByModule } from '../../../../types/permission';
import { UsuarioInterno } from '../../../../types/usuario';
import { PermissionGroup } from './permission-group';

interface Props {
  isOpen: boolean;
  user: UsuarioInterno | null;
  permissions: Permission[];
  loading?: boolean;
  errorMessage?: string | null;
  onConfirm: (keys: string[]) => Promise<unknown> | void;
  onClose: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryLine = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PermissionsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 4px;
`;

const ErrorAlert = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

export const EditPermissionsModal = ({
  isOpen,
  user,
  permissions,
  loading,
  errorMessage,
  onConfirm,
  onClose,
}: Props) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && user) {
      setSelected(new Set(user.permissions));
    }
  }, [isOpen, user]);

  const grouped = useMemo(() => groupByModule(permissions), [permissions]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSave = async () => {
    await onConfirm(Array.from(selected));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Editar permissões"
      description={user ? `Alterar permissões de ${user.name}` : undefined}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={loading}
            startIcon={!loading ? <Check size={14} /> : undefined}
            data-testid="save-permissions-action"
          >
            {loading ? 'Salvando…' : 'Salvar permissões'}
          </Button>
        </>
      }
    >
      {user && (
        <Body>
          <SummaryLine>
            <strong>{selected.size}</strong> de {permissions.length} permissões
            selecionadas.
          </SummaryLine>

          <PermissionsBlock>
            {Object.entries(grouped).map(([module, perms]) => (
              <PermissionGroup
                key={module}
                module={module}
                permissions={perms}
                selected={selected}
                onChange={setSelected}
                disabled={loading}
              />
            ))}
          </PermissionsBlock>

          {errorMessage && <ErrorAlert role="alert">{errorMessage}</ErrorAlert>}
        </Body>
      )}
    </Modal>
  );
};
